<script setup lang="ts">
import { ref, computed } from 'vue'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import { useUserAdminStore } from '@/stores/useUserAdminStore'
import SelectWithAvatar from '@/components/common/selects/SelectWithAvatar.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { MegaphoneIcon } from '@heroicons/vue/24/outline'

const notificationStore = useUserNotificationStore()
const adminStore = useUserAdminStore()

const form = ref({
  title: '',
  description: '',
  userUuids: [] as string[],
  permissions: [] as string[],
  bu: null as string | null,
})

const sending = ref(false)
const successCount = ref<number | null>(null)
const errorMsg = ref<string | null>(null)

const BU_OPTIONS = computed(() =>
  adminStore.bus.map((b) => ({ id: b.value, text: b.label })),
)

const permissionOptions = computed(() => {
  return adminStore.permissions.map((p) => ({
    id: p.code,
    text: p.code,
    subText: p.description || '',
  }))
})

const userOptions = computed(() => {
  return adminStore.usersForManagers.map((u) => ({
    id: u.uuid,
    text: adminStore.getUserLabel(u),
    subText: u.email,
  }))
})

async function sendNotification() {
  if (!form.value.title || !form.value.description) {
    errorMsg.value = 'Le titre et la description sont requis.'
    return
  }

  sending.value = true
  errorMsg.value = null
  successCount.value = null

  try {
    const result = await notificationStore.sendAdminNotification({
      title: form.value.title,
      description: form.value.description,
      userUuids: form.value.userUuids.length > 0 ? form.value.userUuids : undefined,
      permissions: form.value.permissions.length > 0 ? form.value.permissions : undefined,
      bu: form.value.bu,
    })

    if (result && result.success) {
      successCount.value = result.count
      // Reset form but keep some context if needed? No, let's reset.
      form.value = {
        title: '',
        description: '',
        userUuids: [],
        permissions: [],
        bu: null,
      }
    } else {
      errorMsg.value = "Une erreur est survenue lors de l'envoi."
    }
  } catch (err) {
    errorMsg.value = "Erreur de connexion au serveur."
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <div class="card bg-base-100 shadow mt-8">
    <div class="card-body">
      <h2 class="card-title flex items-center gap-2">
        <MegaphoneIcon class="w-6 h-6" />
        Envoi de Notifications Admin
      </h2>
      <p class="text-sm text-base-content/70 mb-4">
        Envoyez une notification personnalisée à un groupe ciblé d'utilisateurs.
        Si plusieurs filtres sont sélectionnés, seuls les utilisateurs remplissant <strong>tous</strong> les critères recevront la notification.
      </p>

      <div v-if="successCount !== null" class="alert alert-success mb-4">
        <span>Notification envoyée avec succès à {{ successCount }} utilisateur(s).</span>
        <button class="btn btn-ghost btn-xs" @click="successCount = null">OK</button>
      </div>

      <div v-if="errorMsg" class="alert alert-error mb-4">
        <span>{{ errorMsg }}</span>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="space-y-4">
          <div class="form-control w-full">
            <label class="label">
              <span class="label-text font-semibold">Titre <span class="text-red-500">*</span></span>
            </label>
            <input
              v-model="form.title"
              type="text"
              placeholder="Titre de la notification"
              class="input input-bordered w-full"
              required
            />
          </div>

          <div class="form-control w-full">
            <label class="label">
              <span class="label-text font-semibold">Message <span class="text-red-500">*</span></span>
            </label>
            <textarea
              v-model="form.description"
              class="textarea textarea-bordered h-24 w-full"
              placeholder="Contenu détaillé du message..."
              required
            ></textarea>
          </div>
        </div>

        <div class="space-y-4">
          <SelectWithAvatar
            v-model="form.userUuids"
            label="Utilisateurs spécifiques"
            :options="userOptions"
            :multiple="true"
          />

          <SelectWithAvatar
            v-model="form.permissions"
            label="Par Permissions"
            :options="permissionOptions"
            :multiple="true"
          />

          <div class="form-control w-full">
            <label class="label">
              <span class="label-text font-semibold">Par Business Unit (BU)</span>
            </label>
            <select v-model="form.bu" class="select select-bordered w-full">
              <option :value="null">Toutes les BU</option>
              <option v-for="opt in BU_OPTIONS" :key="opt.id" :value="opt.id">
                {{ opt.text }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="card-actions justify-end mt-6">
        <ButtonDaisy
          text="Envoyer la notification"
          variant="primary"
          :loading="sending"
          @click="sendNotification"
        >
          <template #icon>
            <MegaphoneIcon class="w-5 h-5" />
          </template>
        </ButtonDaisy>
      </div>
    </div>
  </div>
</template>
